import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Socket } from "socket.io-client";
import { useWebhook, releaseWebhookSocket } from "../webhook";
import { useAuth } from "../auth";
import { MessageDtoResponse } from "../api/Appointment/interfaces/appointmentMessage.response";
import { chatFlowLog } from "./chatFlowLog";
import {
  AppointmentChatContextValue,
  AppointmentChatMessage,
  AppointmentChatProviderProps,
  CrossTabChatEvent,
} from "./types";

const AppointmentChatContext = createContext<AppointmentChatContextValue | null>(null);

const getBroadcastChannelName = (appointmentId: number) =>
  `vivase-appointment-chat-${appointmentId}`;

const mergeMessages = (
  current: AppointmentChatMessage[],
  incoming: AppointmentChatMessage[],
): AppointmentChatMessage[] => {
  const map = new Map<number, AppointmentChatMessage>();

  current.forEach((message) => map.set(message.id, message));
  incoming.forEach((message) => map.set(message.id, message));

  return Array.from(map.values()).sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
};

export const AppointmentChatProvider: React.FC<AppointmentChatProviderProps> = ({
  appointmentId,
  flowScope,
  flowRole,
  consultationId,
  isChatOpen,
  onAppointmentFinished,
  children,
}) => {
  const { getUserData } = useAuth();
  const { getSocket } = useWebhook({ room: String(appointmentId) });

  const [messages, setMessages] = useState<AppointmentChatMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState<string | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const userIdRef = useRef(0);
  const isChatOpenRef = useRef(isChatOpen);
  const unreadCountRef = useRef(0);
  const appointmentFinishedHandlerRef = useRef(onAppointmentFinished);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  appointmentFinishedHandlerRef.current = onAppointmentFinished;
  isChatOpenRef.current = isChatOpen;
  unreadCountRef.current = unreadCount;

  const logBase = useMemo(
    () => ({
      role: flowRole,
      consultationId,
      appointmentId,
    }),
    [flowRole, consultationId, appointmentId],
  );

  const logBaseRef = useRef(logBase);
  logBaseRef.current = logBase;

  const resolveCurrentUser = useCallback(() => {
    const user = getUserData();
    if (user.id) {
      userIdRef.current = user.id;
    }
    return user;
  }, [getUserData]);

  const incrementUnreadCount = useCallback(() => {
    setUnreadCount((previous) => {
      const next = previous + 1;
      unreadCountRef.current = next;

      chatFlowLog(flowScope, "Contador de não lidas incrementado", {
        ...logBase,
        action: "chat_unread_increment",
        unreadCount: next,
      });

      return next;
    });
  }, [flowScope, logBase]);

  const incrementUnreadCountRef = useRef(incrementUnreadCount);
  incrementUnreadCountRef.current = incrementUnreadCount;

  const markAsRead = useCallback(() => {
    if (unreadCountRef.current === 0) {
      return;
    }

    setUnreadCount(0);
    unreadCountRef.current = 0;

    chatFlowLog(flowScope, "Mensagens marcadas como lidas", {
      ...logBase,
      action: "chat_mark_as_read",
      unreadCount: 0,
    });

    broadcastChannelRef.current?.postMessage({
      type: "MARK_READ",
      appointmentId,
    } satisfies CrossTabChatEvent);
  }, [appointmentId, flowScope, logBase]);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") {
      return undefined;
    }

    const channel = new BroadcastChannel(getBroadcastChannelName(appointmentId));
    broadcastChannelRef.current = channel;

    channel.onmessage = (event: MessageEvent<CrossTabChatEvent>) => {
      const payload = event.data;
      if (!payload || payload.appointmentId !== appointmentId) {
        return;
      }

      if (payload.type === "MARK_READ") {
        setUnreadCount(0);
        unreadCountRef.current = 0;
      }
    };

    return () => {
      channel.close();
      broadcastChannelRef.current = null;
    };
  }, [appointmentId]);

  useEffect(() => {
    resolveCurrentUser();
  }, [resolveCurrentUser, isChatOpen, isConnected]);

  useEffect(() => {
    if (isChatOpen) {
      markAsRead();
    }
  }, [isChatOpen, markAsRead]);

  useEffect(() => {
    resolveCurrentUser();

    let socketConnection: Socket | null = null;
    let isDisposed = false;
    const roomKey = String(appointmentId);

    const handleIncomingMessage = (message: MessageDtoResponse) => {
      const currentUserId = resolveCurrentUser().id || userIdRef.current;

      setMessages((previous) => {
        if (previous.some((item) => item.id === message.id)) {
          return previous;
        }

        const withoutOptimistic = previous.filter(
          (item) =>
            !(
              item.id < 0 &&
              item.user.id === message.user.id &&
              item.text === message.text
            ),
        );

        const isFromOtherUser = message.user.id !== currentUserId;
        if (isFromOtherUser && !isChatOpenRef.current) {
          incrementUnreadCountRef.current();
        }

        chatFlowLog(flowScope, "Mensagem recebida em tempo real", {
          ...logBaseRef.current,
          action: "chat_message_received",
          messageId: message.id,
          unreadCount: unreadCountRef.current,
        });

        return mergeMessages(withoutOptimistic, [message]);
      });
    };

    const joinRoom = () => {
      socketConnection?.emit("joinRoom", { appointmentId });
    };

    const setupSocket = async () => {
      socketConnection = await getSocket();
      if (isDisposed) {
        return;
      }

      socketRef.current = socketConnection;

      if (socketConnection.connected) {
        setIsConnected(true);
        joinRoom();
      }

      socketConnection.on("connect", () => {
        setIsConnected(true);
        chatFlowLog(flowScope, "Socket do chat conectado", {
          ...logBaseRef.current,
          action: "chat_socket_connected",
          socketId: socketConnection?.id,
        });
        joinRoom();
      });

      socketConnection.on("disconnect", (reason) => {
        setIsConnected(false);
        chatFlowLog(flowScope, "Socket do chat desconectado", {
          ...logBaseRef.current,
          action: "chat_socket_disconnected",
          reason,
        });
      });

      socketConnection.on("connect_error", (error) => {
        if (socketConnection?.connected) {
          setIsConnected(true);
        }

        chatFlowLog(flowScope, "Erro de transporte socket do chat", {
          ...logBaseRef.current,
          action: "chat_socket_connect_error",
          reason: error.message,
        });
      });

      socketConnection.on("reconnect", () => {
        setIsConnected(true);
        joinRoom();
      });

      socketConnection.on("loadMessages", (loadedMessages: MessageDtoResponse[]) => {
        setMessages((previous) => mergeMessages(previous, loadedMessages));
        chatFlowLog(flowScope, "Histórico de mensagens carregado", {
          ...logBaseRef.current,
          action: "chat_load_messages",
          messagesCount: loadedMessages.length,
        });
      });

      socketConnection.on("receiveMessage", handleIncomingMessage);

      socketConnection.on("userTyping", (data: { userId: number; userName: string }) => {
        if (data.userId !== userIdRef.current) {
          setIsTyping(data.userName);
        }
      });

      socketConnection.on("userStoppedTyping", (data: { userId: number }) => {
        if (data.userId !== userIdRef.current) {
          setIsTyping(null);
        }
      });

      if (appointmentFinishedHandlerRef.current) {
        socketConnection.on("appointment-finished", () => {
          appointmentFinishedHandlerRef.current?.();
        });
      }
    };

    void setupSocket();

    return () => {
      isDisposed = true;

      if (socketConnection) {
        socketConnection.off("connect");
        socketConnection.off("disconnect");
        socketConnection.off("connect_error");
        socketConnection.off("reconnect");
        socketConnection.off("loadMessages");
        socketConnection.off("receiveMessage", handleIncomingMessage);
        socketConnection.off("userTyping");
        socketConnection.off("userStoppedTyping");
        socketConnection.off("appointment-finished");
      }

      releaseWebhookSocket(roomKey);
      socketRef.current = null;
      setIsConnected(false);
      setIsTyping(null);
    };
  }, [appointmentId, flowScope, getSocket, resolveCurrentUser]);

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      const socket = socketRef.current;
      const user = resolveCurrentUser();

      if (!trimmed || !socket?.connected || !user.id) {
        return;
      }

      const optimisticMessage: AppointmentChatMessage = {
        id: -Date.now(),
        text: trimmed,
        createdAt: new Date(),
        user: {
          id: user.id,
          name: user.name || "Você",
        },
      };

      setMessages((previous) => mergeMessages(previous, [optimisticMessage]));

      socket.emit("sendMessage", {
        message: trimmed,
        userId: user.id,
        appointmentId,
      });

      socket.emit("stopTyping", { appointmentId, userId: user.id });

      chatFlowLog(flowScope, "Mensagem enviada", {
        ...logBase,
        action: "chat_message_sent",
        messageId: optimisticMessage.id,
      });
    },
    [appointmentId, flowScope, logBase, resolveCurrentUser],
  );

  const emitTyping = useCallback(
    (userName: string) => {
      const user = resolveCurrentUser();
      if (!user.id) {
        return;
      }

      socketRef.current?.emit("typing", {
        appointmentId,
        userId: user.id,
        userName,
      });
    },
    [appointmentId, resolveCurrentUser],
  );

  const emitStopTyping = useCallback(() => {
    const user = resolveCurrentUser();
    if (!user.id) {
      return;
    }

    socketRef.current?.emit("stopTyping", {
      appointmentId,
      userId: user.id,
    });
  }, [appointmentId, resolveCurrentUser]);

  const value = useMemo<AppointmentChatContextValue>(
    () => ({
      messages,
      unreadCount,
      isConnected,
      isTyping,
      sendMessage,
      markAsRead,
      emitTyping,
      emitStopTyping,
    }),
    [
      messages,
      unreadCount,
      isConnected,
      isTyping,
      sendMessage,
      markAsRead,
      emitTyping,
      emitStopTyping,
    ],
  );

  return (
    <AppointmentChatContext.Provider value={value}>
      {children}
    </AppointmentChatContext.Provider>
  );
};

export const useAppointmentChat = (): AppointmentChatContextValue => {
  const context = useContext(AppointmentChatContext);

  if (!context) {
    throw new Error("useAppointmentChat deve ser usado dentro de AppointmentChatProvider");
  }

  return context;
};
